import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { finalize } from "rxjs/operators";
import Swal from "sweetalert2";
import { Divisa } from "../models/Divisa";
import { CodigosAduana } from "../models/Dte/Exportacion/CodigosAduana";
import { ComisionRecargo } from "../models/Dte/ComisionRecargo";
import { DetalleDte } from "../models/Dte/DetalleDte";
import { DteEmitido } from "../models/Dte/DteEmitido";
import { ExportacionTransporteDte } from "../models/Dte/exportacionDte";
import { FormatoDte } from "../models/Dte/FormatoDte";
import { IDte } from "../models/Dte/IDte";
import { InvoiceType } from "../models/Dte/invoiceType.enum";
import { OptionsReferenceDteEmitido } from "../models/Dte/OptionsReferenceDteEmitido";
import { OptionsTransportDteEmitido } from "../models/Dte/OptionsTransportDteEmitido";
import { PlantillaDte } from "../models/Dte/PlantillaDte";
import { ReferenciaDteEmitido } from "../models/Dte/ReferenciaDteEmitido";
import { TotalDteEmitido } from "../models/Dte/TotalDteEmitido";
import { NotaCredito } from "../models/NotaCredito";
import { NotaDebito } from "../models/NotaDebito";
import { Parametros } from "../models/Parametros";
import { Producto } from "../models/Producto/Producto";
import { ProductoFilter } from "../models/Producto/ProductoFilter";
import { Proveedor } from "../models/Proveedor/Proveedor";
import { Receptor } from "../models/Receptor/Receptor";
import { InvoicingButton } from "../shared/models/InvoicingButton.enum";
import { Page } from "../shared/models/Page";
import { PagedResponse } from "../shared/models/PagedResponse";
import { Response } from "../shared/models/Response";
import { FacturacionIndividualApiService } from "./facturacion-individual-api.service";
import { FacturadorIndividualStateService } from "./facturador-individual-state.service";
import { ProductoService } from "./producto.service";

@Injectable({ providedIn: "root" })
export class FacturacionIndividualService {
	readonly detalleDteListObservable = this.state.detalles$;
	readonly detalleComisionDteListObservable = this.state.comisiones$;
	readonly resumenTotalesObservable = this.state.totales$;
	readonly clienteIdObservable = this.state.clienteId$;
	readonly opcionesReferenciaObservable = this.state.referencias$;
	readonly opcionesTransporteObservable = this.state.transporte$;
	readonly formatSizeObservable = this.state.formato$;
	readonly formatSizeListObservable = this.state.formatos$;
	readonly loadingFormatSizesObservable = this.state.formatoLoading$;
	readonly invalidSizeObservable = this.state.tamanioInvalido$;
	readonly submittedDteObservable = this.state.enviado$;
	readonly divisasListObservable = this.state.divisas$;
	readonly clearListObservable = this.state.limpiarLista$;
	readonly currentInvoiceTypeObservable = this.state.tipoDte$;
	readonly detailWithTaxInOptionsObservable = this.state.detalleConImpuesto$;
	readonly exportacionTransporteObservable = this.state.exportacion$;
	readonly buttonActionsObservable = this.state.acciones$;
	readonly isLoadingCodigosAduana = this.state.codigosAduanaLoading$;
	readonly isLoadingClientObservable = this.state.clienteLoading$;
	readonly resetingClientObservable = this.state.reiniciarCliente$;
	readonly clientIsUnloadedObservable = this.state.clienteDescargado$;
	readonly referencesListFromGuidesObservable = this.state.referenciasGuias$;
	readonly detailsListFromGuidesObservable = this.state.detallesGuias$;
	readonly addDetailProductObservable = this.state.agregarProducto$;
	readonly addDetailDocumentObservable = this.state.agregarDocumento$;
	readonly tipoCantidadFolioObservable = this.state.folios$;
	readonly notaDebitoObservable = this.state.notaDebito$;
	readonly notaCreditoObservable = this.state.notaCredito$;
	readonly selectedClientObservable = this.state.cliente$;
	readonly selectedProveedorObservable = this.state.proveedor$;

	productsPage: Page = Object.assign(new Page(), { pageSize: 20 });
	productsFilter: ProductoFilter = new ProductoFilter();
	fullProductsList: Producto[] = [];

	constructor(
		private api: FacturacionIndividualApiService,
		private productService: ProductoService,
		private state: FacturadorIndividualStateService,
	) {}

	get selectedClient(): Receptor | undefined {
		return this.state.selectedClient;
	}
	set selectedClient(value: Receptor | undefined) {
		this.state.selectedClient = value;
	}
	get selectedProveedor(): Proveedor | undefined {
		return this.state.selectedProveedor;
	}
	set selectedProveedor(value: Proveedor | undefined) {
		this.state.selectedProveedor = value;
	}
	get invalidSize(): boolean {
		return this.state.invalidSize;
	}
	get currentSize(): number {
		return this.state.currentSize;
	}
	get currentInvoiceType(): InvoiceType {
		return this.state.currentInvoiceType;
	}

	clearAllDetails(): void {
		this.state.clearAllDetails();
	}
	setList(list: DetalleDte[]): void {
		this.state.setList(list);
	}
	setComisionList(list: ComisionRecargo[]): void {
		this.state.setComisionList(list);
	}
	setItem(item: DetalleDte): void {
		this.state.agregarDetalle(item);
	}
	setComisionItem(item: ComisionRecargo): void {
		this.state.agregarComision(item);
	}
	setNewItem(item?: DetalleDte): void {
		this.state.agregarNuevoDetalle(item);
	}
	setComisionNewItem(): void {
		this.state.agregarNuevaComision();
	}
	cleanListIfFirstItemEmpty(): void {
		this.state.limpiarPrimerDetalleVacio();
	}
	checkIndexOfEmptyItem(): number {
		return this.state.indiceDetalleVacio();
	}
	updateItem(item: DetalleDte): void {
		this.state.actualizarDetalle(item);
	}
	updateComisionItem(item: ComisionRecargo): void {
		this.state.actualizarComision(item);
	}
	deleteItem(index: number): void {
		this.state.eliminarDetalle(index);
	}
	deleteComisionItem(index: number): void {
		this.state.eliminarComision(index);
	}

	searchClient(rut: string, emisorId: string): Observable<Response<Receptor[]>> {
		this.state.setLoadingCliente(true);
		return this.api.buscarCliente(rut, emisorId).pipe(finalize(() => this.state.setLoadingCliente(false)));
	}

	validateClient(rut: string, emisorId: string): Observable<Response<boolean>> {
		this.state.setLoadingCliente(true);
		return this.api.validarCliente(rut, emisorId).pipe(finalize(() => this.state.setLoadingCliente(false)));
	}

	searchProveedor(rut: string, emisorId: string): Observable<Response<Proveedor[]>> {
		this.state.setLoadingCliente(true);
		return this.api.buscarProveedor(rut, emisorId).pipe(finalize(() => this.state.setLoadingCliente(false)));
	}

	updateClient(rut: string, client: Receptor | undefined): void {
		this.state.updateClient(rut, client);
	}
	updateProveedor(rut: string, proveedor: Proveedor | undefined): void {
		this.state.updateProveedor(rut, proveedor);
	}
	updateOptionsTransport(options: OptionsTransportDteEmitido): void {
		this.state.updateOptionsTransport(options);
	}
	updateOptionsReferences(options: OptionsReferenceDteEmitido): void {
		this.state.updateOptionsReferences(options);
	}
	updateTotales(totales: TotalDteEmitido): void {
		this.state.updateTotales(totales);
	}
	updateOptions(_opciones: OptionsReferenceDteEmitido): void {}
	updateDivisas(divisas: Divisa[]): void {
		this.state.updateDivisas(divisas);
	}
	updateFormatoDte(formatos: FormatoDte[]): void {
		this.state.updateFormatoDte(formatos);
	}

	getFormatSizeItems(emisorId: string, tipoDteId: string): Observable<Response<FormatoDte[]>> {
		this.state.setLoadingFormatos(true);
		return this.api.obtenerFormatos(emisorId, tipoDteId).pipe(finalize(() => this.state.setLoadingFormatos(false)));
	}

	checkValidList(): void {
		this.state.setListSnapshotValidation();
	}
	checkValidComisionList(): void {
		this.state.setCommissionSnapshotValidation();
	}
	resetElements(): void {
		this.state.resetElements();
	}
	dteSubmitted(): void {
		this.state.marcarEnviado(true);
	}
	resetSubmit(): void {
		this.state.marcarEnviado(false);
	}
	clearUnfilledItems(): void {
		this.state.limpiarDetallesSinCantidad();
	}
	clearUnfilledComisionItems(): void {
		this.state.limpiarComisionesSinCantidad();
	}
	countLinesInList(list: DetalleDte[]): number {
		return this.state.contarLineasDetalles(list);
	}
	countLinesInComisionList(list: ComisionRecargo[]): number {
		return this.state.contarLineasComisiones(list);
	}
	setCurrentInvoiceType(type: InvoiceType): void {
		this.state.setCurrentInvoiceType(type);
	}

	searchProducts(searchTerm: string, searchValue: string, emisorId: string): Observable<PagedResponse<Producto>> {
		const filter = new ProductoFilter();
		filter.emisorId = emisorId;
		filter[searchTerm] = searchValue;
		return this.productService.getDataByPage(filter, Object.assign(new Page(), this.productsPage));
	}

	checkOptionsDetalleMode(options: OptionsReferenceDteEmitido, list: DetalleDte[]): void {
		if (!list.some((detail) => detail.totalImpuestosAdicionales > 0) || !options.sonDetallesConIVA) return;
		Swal.fire({
			icon: "error",
			title: "Error en detalles",
			text: "El documento solo considera montos netos si los productos tienen impuestos específicos asociados",
		});
		options.sonDetallesConIVA = false;
		this.state.setDetailWithTaxInOptions(false);
	}

	updateExportacionTransporte(exportacion: ExportacionTransporteDte): void {
		this.state.updateExportacionTransporte(exportacion);
	}

	getCodigosAduana(): Observable<Response<CodigosAduana>> {
		this.state.setLoadingCodigosAduana(true);
		return this.api.obtenerCodigosAduana().pipe(finalize(() => this.state.setLoadingCodigosAduana(false)));
	}

	sendDte(usuarioId: string, sucursalId: string, dte: IDte): Observable<Response<string>> {
		return this.api.emitirFactura(usuarioId, sucursalId, dte);
	}
	sendDteGuide(usuarioId: string, sucursalId: string, dte: IDte): Observable<Response<string>> {
		return this.api.emitirGuia(usuarioId, sucursalId, dte);
	}
	sendDteSettlement(usuarioId: string, sucursalId: string, dte: IDte): Observable<Response<string>> {
		return this.api.emitirLiquidacion(usuarioId, sucursalId, dte);
	}
	sendDteBoleta(usuarioId: string, sucursalId: string, dte: IDte): Observable<Response<string>> {
		return this.api.emitirBoleta(usuarioId, sucursalId, dte);
	}
	getParametros(): Observable<Response<Parametros>> {
		return this.api.obtenerParametrosBoleta();
	}
	previewDte(usuarioId: string, sucursalId: string, dte: IDte): Observable<Response<string>> {
		return this.api.previsualizar(usuarioId, sucursalId, dte);
	}
	sendFacturaCompraRequest(usuarioId: string, sucursalId: string, dte: IDte): Observable<Response<any>> {
		return this.api.emitirFacturaCompra(usuarioId, sucursalId, dte);
	}
	sendNotaDebito(usuarioId: string, sucursalId: string, nota: NotaDebito, dte: DteEmitido): Observable<Response<any>> {
		return this.api.emitirNotaDebito(usuarioId, sucursalId, nota, dte);
	}
	sendNotaCredito(usuarioId: string, sucursalId: string, nota: NotaCredito, dte: DteEmitido): Observable<Response<any>> {
		return this.api.emitirNotaCredito(usuarioId, sucursalId, nota, dte);
	}
	sendFacturaExportacion(usuarioId: string, sucursalId: string, dte: DteEmitido): Observable<Response<any>> {
		return this.api.emitirExportacion(usuarioId, sucursalId, dte);
	}

	sendEmitAction(): void {
		this.state.enviarAccion(InvoicingButton.Emitir);
	}
	sendPreviewAction(): void {
		this.state.enviarAccion(InvoicingButton.Previsualizar);
	}
	sendCancelAction(): void {
		this.state.enviarAccion(InvoicingButton.Cancelar);
	}
	sendFacturaGuiaAction(): void {
		this.state.enviarAccion(InvoicingButton.FacturarGuias);
	}
	sendEmitAndSaveAction(): void {
		this.state.enviarAccion(InvoicingButton.EmitirYGuardarFrecuente);
	}

	getPlantillas(emisorId: string, codigosTipoDte?: number[]): Observable<Response<PlantillaDte[]>> {
		return this.api.obtenerPlantillas(emisorId, codigosTipoDte);
	}
	guardarComoPlantilla(dteId: string, nombre: string): Observable<Response<boolean>> {
		return this.api.actualizarPlantilla(dteId, true, nombre);
	}
	eliminarPlantilla(dteId: string): Observable<Response<boolean>> {
		return this.api.actualizarPlantilla(dteId, false, "");
	}
	checkForBillableGuides(emisorId: string, receptorId: string, usuarioId: string): Observable<Response<DteEmitido[]>> {
		return this.api.obtenerGuiasFacturables(emisorId, receptorId, usuarioId);
	}

	passTipoCantidadFolio(info: [string, number]): void {
		this.state.publicarFolios(info);
	}
	publishReferencesFromGuides(references: ReferenciaDteEmitido[]): void {
		this.state.publicarReferenciasGuias(references);
	}
	publishDetailsFromGuides(details: DetalleDte[]): void {
		this.state.publicarDetallesGuias(details);
	}
	publishProductDetail(detail: DetalleDte | undefined): void {
		this.state.publicarProducto(detail);
	}
	publishDocumentDetail(detail: DetalleDte | undefined): void {
		this.state.publicarDocumento(detail);
	}
}
